// components/IEP/TransformationDetail.js
import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  LinearProgress,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Close,
  Download,
  Share,
  Timeline,
  AttachMoney,
  Group,
  Schedule,
  Psychology,
  TrendingUp,
  Warning,
  CheckCircle,
  Info,
  ExpandMore,
  ArrowForward,
  AutoGraph,
  CompareArrows,
  Star,
  StarBorder,
  Check,
  Add
} from '@mui/icons-material';
import { useIEP } from '../../contexts/IEPContext';

const TransformationDetail = ({ transformation, onClose, onAddToRoadmap }) => {

const { addToRoadmap, removeFromRoadmap, isInRoadmap } = useIEP(); // ✅ ADDED
const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const inRoadmap = isInRoadmap(transformation.id); // ✅ CHECK


  const [activeTab, setActiveTab] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const { iepEngine, organization } = useIEP();

  if (!transformation) return null;

  const handleAddToRoadmap = () => {
    try {
      if (inRoadmap) {
        const result = removeFromRoadmap(transformation.id);
        if (result?.success) {
          setSnackbar({
            open: true,
            message: 'Removed from roadmap',
            severity: 'info'
          });
        }
      } else {
        const result = addToRoadmap(transformation);
        if (result?.success) {
          setSnackbar({
            open: true,
            message: 'Added to roadmap successfully!',
            severity: 'success'
          });
          // Optionally close dialog after adding
          // setTimeout(() => onClose(), 1500);
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update roadmap',
        severity: 'error'
      });
    }
  };

  const handleToggleFavorite = () => {
    setIsFavorite(!isFavorite);
    // In a real app, this would update in context/backend
  };

  const calculateROI = () => {
    if (!iepEngine || !organization) return transformation.estimatedROI || '3.5x';
    return iepEngine.estimateROI(transformation, organization);
  };

  const tabs = [
    { label: 'Overview', value: 0 },
    { label: 'Implementation', value: 1 },
    { label: 'Metrics', value: 2 },
    { label: 'Resources', value: 3 },
    { label: 'Q* AI Analysis', value: 4 }
  ];

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getComplexityColor = (complexity) => {
    switch(complexity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  return (
    <Dialog 
      open={!!transformation} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <Typography variant="h5" fontWeight="700">
              {transformation.name}
            </Typography>
            <Box ml={2} display="flex" gap={1}>
              <Chip 
                label={transformation.category}
                color="primary"
                size="small"
                variant="outlined"
              />
              <Chip 
                label={transformation.priority}
                color={getPriorityColor(transformation.priority)}
                size="small"
                icon={transformation.priority === 'critical' ? <Warning /> : null}
              />
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
              <IconButton onClick={handleToggleFavorite} size="small">
                {isFavorite ? <Star color="warning" /> : <StarBorder />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Tabs */}
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          sx={{ mb: 3 }}
        >
          {tabs.map(tab => (
            <Tab key={tab.value} label={tab.label} />
          ))}
        </Tabs>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {/* Description */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom color="primary">
                  Description
                </Typography>
                <Typography paragraph>
                  {transformation.description}
                </Typography>
              </Box>

              {/* Key Benefits */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom color="primary">
                  Key Benefits
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Performance Improvement
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {transformation.metrics?.performanceImprovement?.target || '40%'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Efficiency Gains
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {transformation.metrics?.efficiencyGains?.target || '35%'}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Success Criteria */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom color="primary">
                  Success Criteria
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography>
                    {transformation.successCriteria}
                  </Typography>
                </Paper>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              {/* Quick Stats */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Quick Stats
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Schedule color="info" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Timeline"
                      secondary={`${transformation.expectedDuration?.min}-${transformation.expectedDuration?.max} days`}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <AttachMoney color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Budget Range"
                      secondary={`$${(transformation.resourceRequirements?.budget?.min / 1000).toFixed(0)}K - $${(transformation.resourceRequirements?.budget?.max / 1000).toFixed(0)}K`}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <Group color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Team Size"
                      secondary={`${transformation.resourceRequirements?.teamSize?.core} core + ${transformation.resourceRequirements?.teamSize?.support} support`}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <TrendingUp color="warning" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Expected ROI"
                      secondary={`${calculateROI()} return on investment`}
                    />
                  </ListItem>
                </List>
              </Paper>

              {/* Complexity & Layer */}
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  Transformation Profile
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Complexity
                      </Typography>
                      <Chip 
                        label={transformation.complexity}
                        size="small"
                        color={getComplexityColor(transformation.complexity)}
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Layer
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        {transformation.layer}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* AI Insights */}
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2,
                  background: 'linear-gradient(145deg, #6366f110, #8b5cf610)',
                  borderColor: 'primary.light'
                }}
              >
                <Typography variant="subtitle2" gutterBottom color="primary">
                  <Psychology sx={{ verticalAlign: 'middle', mr: 1, fontSize: '1rem' }} />
                  AI Insight
                </Typography>
                <Typography variant="body2">
                  This transformation is highly recommended for organizations at your 
                  maturity level. It addresses critical gaps in your current capabilities 
                  and has a high probability of success.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Implementation Steps
            </Typography>
            
            {transformation.implementationSteps?.map((step, index) => (
              <Accordion key={index} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" width="100%">
                    <Box 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.main', 
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        fontWeight: 'bold'
                      }}
                    >
                      {step.step}
                    </Box>
                    <Box flex={1}>
                      <Typography variant="subtitle1">
                        {step.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Duration: {step.duration} days
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph>
                    {step.description}
                  </Typography>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Deliverables:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    {step.deliverables?.map((deliverable, idx) => (
                      <Chip 
                        key={idx}
                        label={deliverable}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Key Activities:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Initial assessment and planning" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Stakeholder alignment workshops" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Implementation and integration" />
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}

            <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                <Timeline sx={{ verticalAlign: 'middle', mr: 1 }} />
                Implementation Timeline
              </Typography>
              <Box display="flex" alignItems="center" height={40}>
                {transformation.implementationSteps?.map((step, index) => (
                  <Tooltip key={index} title={`Step ${step.step}: ${step.name} (${step.duration} days)`}>
                    <Box
                      sx={{
                        flex: step.duration,
                        height: 20,
                        bgcolor: 'primary.main',
                        opacity: 0.7 + (index * 0.1),
                        mr: index < transformation.implementationSteps.length - 1 ? 1 : 0,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                          opacity: 1,
                          transform: 'scale(1.05)'
                        }
                      }}
                    >
                      {step.step}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Paper>
          </Box>
        )}

        {activeTab === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {/* Performance Metrics */}
              <Typography variant="h6" gutterBottom color="primary">
                Performance Metrics
              </Typography>
              
              {transformation.metrics && Object.entries(transformation.metrics).map(([key, metric]) => (
                <Paper key={key} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" textTransform="capitalize">
                      {key.replace(/([A-Z])/g, ' $1')}
                    </Typography>
                    <Chip 
                      label={metric.importance}
                      size="small"
                      color={metric.importance === 'critical' ? 'error' : metric.importance === 'high' ? 'warning' : 'info'}
                    />
                  </Box>
                  
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        Target: {metric.target}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Benchmark: {metric.benchmark}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={parseInt(metric.target) || 50}
                      sx={{ height: 8, borderRadius: 4 }}
                      color="success"
                    />
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary">
                    Unit: {metric.unit}
                  </Typography>
                </Paper>
              ))}
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Measurement Approach */}
              <Typography variant="h6" gutterBottom color="primary">
                Measurement Approach
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Data Collection
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Automated data collection from systems" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Monthly stakeholder surveys" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Quarterly performance reviews" />
                  </ListItem>
                </List>
              </Paper>

              {/* Success Indicators */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Success Indicators
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Indicator</TableCell>
                        <TableCell align="right">Threshold</TableCell>
                        <TableCell align="right">Frequency</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Employee Adoption</TableCell>
                        <TableCell align="right">≥80%</TableCell>
                        <TableCell align="right">Monthly</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Process Efficiency</TableCell>
                        <TableCell align="right">≥30% improvement</TableCell>
                        <TableCell align="right">Quarterly</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Cost Savings</TableCell>
                        <TableCell align="right">≥20% reduction</TableCell>
                        <TableCell align="right">Quarterly</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Customer Satisfaction</TableCell>
                        <TableCell align="right">≥85%</TableCell>
                        <TableCell align="right">Quarterly</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeTab === 3 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {/* Resource Requirements */}
              <Typography variant="h6" gutterBottom color="primary">
                Resource Requirements
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Budget Breakdown
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="primary.main">
                        ${(transformation.resourceRequirements?.budget?.min / 1000).toFixed(0)}K
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Minimum
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="primary.main">
                        ${(transformation.resourceRequirements?.budget?.max / 1000).toFixed(0)}K
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Maximum
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Team Structure */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Team Structure
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Group color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Core Team"
                      secondary={`${transformation.resourceRequirements?.teamSize?.core || 3} members (full-time)`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Group color="secondary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Support Team"
                      secondary={`${transformation.resourceRequirements?.teamSize?.support || 5} members (part-time)`}
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Timeline */}
              <Typography variant="h6" gutterBottom color="primary">
                Timeline Breakdown
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Schedule color="info" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Preparation Phase"
                      secondary={`${transformation.resourceRequirements?.timeline?.preparation || 30} days`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Schedule color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Execution Phase"
                      secondary={`${transformation.resourceRequirements?.timeline?.execution || 90} days`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Schedule color="warning" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Stabilization Phase"
                      secondary={`${transformation.resourceRequirements?.timeline?.stabilization || 30} days`}
                    />
                  </ListItem>
                </List>
              </Paper>

              {/* Dependencies */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Dependencies & Prerequisites
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Warning color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Stakeholder buy-in and sponsorship" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Warning color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Baseline data collection complete" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Warning color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Core team availability confirmed" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeTab === 4 && (
          <Box>
            {/* Q* AI Analysis */}
            <Typography variant="h6" gutterBottom color="primary">
              <Psychology sx={{ verticalAlign: 'middle', mr: 1 }} />
              Q* AI Analysis
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3,
                    background: 'linear-gradient(145deg, #6366f110, #8b5cf610)',
                    borderColor: 'primary.light'
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    AI Optimization Score
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="center" my={3}>
                    <Box
                      sx={{
                        position: 'relative',
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        background: `conic-gradient(#10b981 ${transformation.impactScore * 100}%, #e5e7eb 0)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          width: 90,
                          height: 90,
                          borderRadius: '50%',
                          bgcolor: 'background.paper',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column'
                        }}
                      >
                        <Typography variant="h4" fontWeight="700">
                          {(transformation.impactScore * 100).toFixed(0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Score
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="body2" textAlign="center">
                    This transformation ranks in the top 20% of recommendations 
                    based on your organization's specific context and constraints.
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Why This Transformation?
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="High Alignment with Your Strategic Goals"
                        secondary="Matches 8 out of 10 of your stated objectives"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Optimal Resource Utilization"
                        secondary="Fits within your budget and team constraints"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Proven Success Pattern"
                        secondary="92% success rate in similar organizations"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="High ROI Potential"
                        secondary={`Expected ${calculateROI()} return on investment`}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
            </Grid>

            {/* AI Recommendations */}
            <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom color="primary">
                <AutoGraph sx={{ verticalAlign: 'middle', mr: 1 }} />
                AI Implementation Recommendations
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Recommended Timing
                    </Typography>
                    <Typography variant="body2">
                      Start within next 30 days for optimal impact
                    </Typography>
                    <Chip 
                      label="Urgent"
                      size="small"
                      color="error"
                      sx={{ mt: 1 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Team Composition
                    </Typography>
                    <Typography variant="body2">
                      Include 2-3 subject matter experts from operations
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Key Success Factors
                    </Typography>
                    <Typography variant="body2">
                      Executive sponsorship and weekly progress reviews
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ mr: 1 }}
          >
            Export Details
          </Button>
          <Button
            variant="outlined"
            startIcon={<Share />}
          >
            Share
          </Button>
        </Box>
        <Box>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ mr: 2 }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<Timeline />}
            onClick={handleAddToRoadmap}
          >
            Add to Roadmap
          </Button>
		    <Button
    variant="contained"
    startIcon={inRoadmap ? <Check /> : <Add />}
    color={inRoadmap ? "success" : "primary"}
    onClick={handleAddToRoadmap}
  >
    {inRoadmap ? 'In Roadmap' : 'Add to Roadmap'}
  </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TransformationDetail;